import { useState, type FormEvent } from "react";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import type { PublicMapArea } from "@natural/public-contract";
import { accountApi, relationLabels, type OrganizationDetail } from "../../account";
import { OrganizationAreaClaimsStep } from "../components/OrganizationAreaClaimsStep";
import { OrganizationAuthorityStep } from "../components/OrganizationAuthorityStep";
import { OrganizationIdentityStep } from "../components/OrganizationIdentityStep";
import { OrganizationReviewStep } from "../components/OrganizationReviewStep";
import { OrganizationServicesStep } from "../components/OrganizationServicesStep";
import { useOrganizationAreaClaims } from "../hooks/useOrganizationAreaClaims";

export function OrganizationApplicationSection({
  slug,
  data,
  areas,
  onSaved,
}: {
  slug: string;
  data: OrganizationDetail;
  areas: PublicMapArea[];
  onSaved: (message: string) => void;
}) {
  const [error, setError] = useState(""),
    application = data.application,
    status = String(application?.status ?? ""),
    correctionRequested =
      application?.identity_status === "rejected" ||
      application?.representation_status === "rejected" ||
      data.areaClaims.some((claim) => claim.status === "rejected"),
    canSubmit =
      ["owner", "admin"].includes(data.organization.role) &&
      (correctionRequested ||
        !["submitted", "under_review", "approved"].includes(status));
  const areaClaims = useOrganizationAreaClaims(areas);
  const { claimCount } = areaClaims;
  const type =
      data.organization.declared_type ||
      data.organization.organization_type ||
      data.organization.organizationType ||
      "",
    defaultRelationship =
      type === "fishery_area"
        ? "fishery_area"
        : type === "permit_issuer"
          ? "permit_issuer"
          : ["permit_seller", "reseller"].includes(type)
            ? "permit_seller"
            : type === "service_provider"
              ? "service_provider"
              : "owner";
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    const values = new FormData(event.currentTarget),
      areaClaims = Array.from({ length: claimCount }, (_, index) => ({
        coreAreaId: values.get(`claim.${index}.coreAreaId`),
        requestedAreaName: values.get(`claim.${index}.requestedAreaName`),
        municipality: values.get(`claim.${index}.municipality`),
        relationship: values.get(`claim.${index}.relationship`),
        claimBasis: values.get(`claim.${index}.claimBasis`),
        evidenceUrl: values.get(`claim.${index}.evidenceUrl`),
        propertyReference: values.get(`claim.${index}.propertyReference`),
        validFrom: values.get(`claim.${index}.validFrom`),
        validTo: values.get(`claim.${index}.validTo`),
        scopeNote: values.get(`claim.${index}.scopeNote`),
        areaKind: values.get(`claim.${index}.areaKind`),
        parentCoreAreaId: values.get(`claim.${index}.parentCoreAreaId`),
        placeCandidate: (() => {
          try {
            return JSON.parse(
              String(values.get(`claim.${index}.placeCandidate`) ?? "null"),
            );
          } catch {
            return null;
          }
        })(),
      }));
    try {
      await accountApi(
        `/api/account/organizations/${encodeURIComponent(slug)}/application`,
        {
          method: "POST",
          body: JSON.stringify({
            servicePlanId: values.get("servicePlanId"),
            municipality: values.get("municipality"),
            declaredEntityType: values.get("declaredEntityType"),
            officialRegisterId: values.get("officialRegisterId"),
            domicile: values.get("domicile"),
            websiteUrl: values.get("websiteUrl"),
            contactPhone: values.get("contactPhone"),
            representativeName: values.get("representativeName"),
            representativeTitle: values.get("representativeTitle"),
            authorityBasis: values.get("authorityBasis"),
            authorityEvidenceUrl: values.get("authorityEvidenceUrl"),
            authorityValidUntil: values.get("authorityValidUntil"),
            requestedServices: values.getAll("requestedServices"),
            billingContactName: values.get("billingContactName"),
            billingContactEmail: values.get("billingContactEmail"),
            applicantNote: values.get("applicantNote"),
            areaClaims,
          }),
        },
      );
      onSaved(
        "Hakemus lähetettiin. Identiteetti, edustusoikeus ja jokainen aluevaatimus tarkistetaan erikseen; maksua ei veloitettu.",
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Hakemusta ei voitu lähettää.",
      );
    }
  };
  const statusText: Record<string, string> = {
    submitted: "Hakemus odottaa käsittelyä",
    under_review: "Hakemus on tarkastuksessa",
    approved: "Hakemus on hyväksytty",
    rejected: "Hakemus palautettiin korjattavaksi",
  };
  const checkLabel = (value: unknown) =>
    value === "approved"
      ? "hyväksytty"
      : value === "rejected"
        ? "korjattava"
        : "odottaa tarkastusta";
  return (
    <section className="account-panel workspace-section organization-application">
      <div className="workspace-section-heading">
        <div>
          <span className="eyebrow">Organisaation käyttöönotto</span>
          <h2>
            {status
              ? (statusText[status] ?? "Hakemus")
              : "Lähetä käyttöönottohakemus"}
          </h2>
          <p>
            Täytä alla oleva lomake huolellisesti. Käsittelemme hakemuksen
            vaiheittain.
          </p>
        </div>
        <ShieldCheck />
      </div>
      <div className="application-stage-track">
        {[
          "Organisaation tiedot",
          "Edustustiedot",
          "Palvelut ja luvat",
          "Alueet ja toimivalta",
          "Tarkista ja lähetä",
        ].map((label, index) => (
          <span className={index === 0 ? "current" : ""} key={label}>
            <i>{index + 1}</i>
            <b>{label}</b>
          </span>
        ))}
      </div>
      {application && (
        <>
          <div className={`application-status ${status}`}>
            <b>
              {String(application.service_plan_name)} ·{" "}
              {String(application.claimed_municipality)}
            </b>
            <span>{statusText[status] ?? status}</span>
            {Boolean(application.decision_note) && (
              <p>{String(application.decision_note)}</p>
            )}
          </div>
          <div className="application-checks">
            <span>Organisaatio: {checkLabel(application.identity_status)}</span>
            <span>
              Edustusoikeus: {checkLabel(application.representation_status)}
            </span>
            <span>
              Aluevaatimukset:{" "}
              {
                data.areaClaims.filter((claim) => claim.status === "approved")
                  .length
              }
              /{data.areaClaims.length}
            </span>
          </div>
        </>
      )}
      {data.areaClaims.map((claim) => (
        <div className="application-claim" key={String(claim.id)}>
          <MapPin />
          <div>
            <b>{String(claim.requested_area_name)}</b>
            <span>
              {relationLabels[String(claim.relationship)] ??
                String(claim.relationship)}{" "}
              ·{" "}
              {claim.core_area_id
                ? `Core-alue ${String(claim.core_area_id)}`
                : "uusi alueluonnos voi olla tarpeen"}{" "}
              · {String(claim.status)}
            </span>
            {Boolean(claim.review_note) && (
              <small>{String(claim.review_note)}</small>
            )}
          </div>
        </div>
      ))}
      {canSubmit ? (
        <form
          className="application-form detailed-application-form"
          onSubmit={submit}
        >
          <OrganizationIdentityStep data={data} organizationType={type} />
          <OrganizationAuthorityStep />
          <OrganizationServicesStep application={application} data={data} />
          <OrganizationAreaClaimsStep
            areas={areas}
            controller={areaClaims}
            defaultRelationship={defaultRelationship}
          />
          <OrganizationReviewStep claimCount={claimCount} data={data} />
          <label className="wide-field application-notes">
            Hakemuksen lisätiedot
            <textarea name="applicantNote" maxLength={2000} rows={3} />
          </label>
          {error && <p className="account-error wide-field">{error}</p>}
          <div className="wide-field application-boundary">
            <ShieldCheck />
            <span>
              Maksutili valmistellaan vasta hyväksynnän jälkeen. Stripe
              tunnistaa maksunsaajan erikseen, eikä maksaminen vahvista
              alueoikeuksia.
            </span>
          </div>
          <div className="wide-field application-submit-actions">
            <button
              type="button"
              disabled
              title="Luonnostallennus lisätään seuraavassa vaiheessa"
            >
              Tallenna luonnos
            </button>
            <button>
              Lähetä hakemus <ArrowRight />
            </button>
          </div>
        </form>
      ) : (
        !application && (
          <div className="workspace-readonly">
            Vain organisaation omistaja tai ylläpitäjä voi lähettää hakemuksen.
          </div>
        )
      )}
    </section>
  );
}
